"use client";

import { Lookup, SpringRef } from "@react-spring/web";
import { useEffect, useState } from "react";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import { MagnoliaImage } from "@/components/atoms/image/image";
import QuestionDetailSection from "@/components/molecules/question-detail-section/question-detail-section";
import TrustPilot from "@/components/molecules/trustpilot-component/trustpilot";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import withUndiagnosedSymptoms, {
  MedicalConfirmationFlowEnum,
} from "@/components/with-undiagnosed-symptoms";
import {
  QuestionMedicalConfirmation,
  QuestionMedicalEligibility,
  QuestionMedicalTreatments,
} from "@/config/app-constant";
import { useGatekeeperQuestions } from "@/providers/gatekeeper-questions-provider";
import { isMedicalEligibilityFlow } from "@/utils/medical.utils";
import { GatekeeperQuestion } from "@/utils/schema/gatekeeper-question-page-schema.utils";

import Styles from "./q-medical-section.module.css";

function QMedicalSupportContent({
  qMedicalContent,
  showondesktop = "false",
  routeName,
  newMedicalFlowEnabled,
  supportRef,
  medicalConfirmationFlow,
}: Readonly<{
  qMedicalContent: GatekeeperQuestion;
  showondesktop?: string;
  routeName?: string;
  supportRef?: SpringRef<Lookup>;
  newMedicalFlowEnabled: boolean;
  medicalConfirmationFlow?: MedicalConfirmationFlowEnum;
}>) {
  const { selected, selectedTwo } = useGatekeeperQuestions();
  const [isNewMedicalConfirmationPage, setIsNewMedicalConfirmationPage] =
    useState(false);
  const [isUndiagnosedQuestion, setIsUndiagnosedQuestion] = useState(false);

  const { wizard } = useWizard();
  const { productSpecialFeatures } = wizard;

  useEffect(() => {
    if (
      newMedicalFlowEnabled &&
      productSpecialFeatures?.showMedicalPageBackgroundImage &&
      (routeName === QuestionMedicalConfirmation ||
        routeName === QuestionMedicalTreatments ||
        routeName === QuestionMedicalEligibility)
    )
      setIsNewMedicalConfirmationPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMedicalFlowEnabled, routeName]);

  useEffect(() => {
    if (
      newMedicalFlowEnabled &&
      routeName === QuestionMedicalConfirmation &&
      selected === 2 &&
      selectedTwo === 0 &&
      (medicalConfirmationFlow as string) ===
        MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion
    ) {
      setIsUndiagnosedQuestion(true);
    } else {
      setIsUndiagnosedQuestion(false);
    }
  }, [
    medicalConfirmationFlow,
    newMedicalFlowEnabled,
    routeName,
    selectedTwo,
    selected,
  ]);
  const {
    defaultDescription,
    answerYesDescription,
    answerNoDescription,
    defaultSupportDrawer,
    answerYesSupportDrawer,
    answerNoSupportDrawer,
    defaultReadMoreButtonText,
    answerYesReadMoreButtonText,
    answerNoReadMoreButtonText,
    trustpilot,
    defaultBackgroundImage,
    answerNoBackgroundImage,
    answerYesBackgroundImage,
    answerUndiagnosedBackgroundImage,
    answerUndiagnosedQuestionDescription,
    answerUndiagnosedNoQuestionDescription,
    answerNoUndiagnosedBackgroundImage,
    answerYesUndiagnosedBackgroundImage,
    answerNoUndiagnosedDescription,
    chatButtonLabel,
  } = qMedicalContent;

  const backgroundImageDefault: MagnoliaImage | undefined | null =
    typeof defaultBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": defaultBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : defaultBackgroundImage;

  const backgroundImageAnswerNo: MagnoliaImage | undefined | null =
    typeof answerNoBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": answerNoBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : answerNoBackgroundImage;
  const backgroundImageAnswerYes: MagnoliaImage | undefined | null =
    typeof answerYesBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": answerYesBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : answerYesBackgroundImage;
  const undiagnosedBackgroundImage: MagnoliaImage | undefined | null =
    typeof answerUndiagnosedBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": answerUndiagnosedBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : answerUndiagnosedBackgroundImage;
  const noUndiagnosedBackgroundImage: MagnoliaImage | undefined | null =
    typeof answerNoUndiagnosedBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": answerNoUndiagnosedBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : answerNoUndiagnosedBackgroundImage;
  const yesUndiagnosedBackgroundImage: MagnoliaImage | undefined | null =
    typeof answerYesUndiagnosedBackgroundImage === "string"
      ? {
          "@id": "",
          "@link": answerYesUndiagnosedBackgroundImage,
          "@name": "",
          "@path": "",
          "@type": "",
          "@uuid": "",
        }
      : answerYesUndiagnosedBackgroundImage;
  const srcImage1 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${backgroundImageDefault?.["@id"]}${backgroundImageDefault?.["@path"]}`;
  const srcImage2 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${backgroundImageAnswerNo?.["@id"]}${backgroundImageAnswerNo?.["@path"]}`;
  const srcImage3 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${backgroundImageAnswerYes?.["@id"]}${backgroundImageAnswerYes?.["@path"]}`;
  const srcImage4 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${undiagnosedBackgroundImage?.["@id"]}${undiagnosedBackgroundImage?.["@path"]}`;
  const srcImage5 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${noUndiagnosedBackgroundImage?.["@id"]}${noUndiagnosedBackgroundImage?.["@path"]}`;
  const srcImage6 = `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${yesUndiagnosedBackgroundImage?.["@id"]}${yesUndiagnosedBackgroundImage?.["@path"]}`;

  const renderQuestionDetailSection = () => {
    let props: {
      loadMoreButtonText?: string;
      supportDrawer?: object | null;
      rightContent?: object | null;
      supportImage?: string;
      chatButtonLabel?: string;
    } = {};

    switch (selected) {
      case 1:
        props = {
          loadMoreButtonText: answerYesReadMoreButtonText,
          supportDrawer: answerYesSupportDrawer,
          rightContent: answerYesDescription,
          supportImage: srcImage3,
          chatButtonLabel: chatButtonLabel,
        };
        break;
      case 2:
        if (
          (medicalConfirmationFlow as string) ===
            MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion &&
          selectedTwo === 1 &&
          routeName === QuestionMedicalConfirmation
        ) {
          props = {
            rightContent: answerUndiagnosedQuestionDescription,
            supportImage: srcImage6,
            chatButtonLabel: chatButtonLabel,
          };
        } else if (
          (medicalConfirmationFlow as string) ===
            MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion &&
          selectedTwo === 2 &&
          routeName === QuestionMedicalConfirmation
        ) {
          props = {
            rightContent: answerUndiagnosedNoQuestionDescription,
            supportImage: srcImage5,
            chatButtonLabel: chatButtonLabel,
          };
        } else {
          props = {
            loadMoreButtonText: answerNoReadMoreButtonText,
            supportDrawer: answerNoSupportDrawer,
            rightContent:
              (medicalConfirmationFlow as string) ===
                MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion &&
              routeName === QuestionMedicalConfirmation
                ? answerNoUndiagnosedDescription
                : answerNoDescription,
            supportImage:
              (medicalConfirmationFlow as string) ===
                MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion &&
              routeName === QuestionMedicalConfirmation
                ? srcImage4
                : srcImage2,
            chatButtonLabel: chatButtonLabel,
          };
        }
        break;
      default:
        props = {
          loadMoreButtonText:
            isMedicalEligibilityFlow() &&
            routeName === QuestionMedicalEligibility
              ? undefined
              : defaultReadMoreButtonText,
          supportDrawer: defaultSupportDrawer,
          rightContent: defaultDescription,
          supportImage: srcImage1,
          chatButtonLabel: chatButtonLabel,
        };
        break;
    }

    return (
      <QuestionDetailSection
        key={`qds-${selected}`}
        loadMoreButtonEnable
        loadMoreButtonText={props.loadMoreButtonText}
        supportDrawer={props.supportDrawer}
        rightContent={props.rightContent}
        supportImage={props.supportImage}
        medicalConfirmationPage={isNewMedicalConfirmationPage}
        isUndiagnosedQuestion={isUndiagnosedQuestion}
        chatButtonLabel={props.chatButtonLabel}
      />
    );
  };
  return (
    <div
      data-testid="q-medical-support-content"
      className={` rounded-radius-xxl !bg-${
        isNewMedicalConfirmationPage
          ? "surface-neutral-option-2"
          : "dark-surface-accent-1-primary"
      } flex lg:flex-col 
          flex-col-reverse text-surface-neutral-option-2 h-full overflow-hidden text-left ${
            Styles.supportLink
          }`}
    >
      {!isNewMedicalConfirmationPage &&
        showondesktop === "true" &&
        trustpilot && (
          <TrustPilot
            trustpilotText={trustpilot["0"]?.trustpilotText["0"]}
            trustpilotImage={trustpilot["0"]?.trustpilotImage["0"]}
          />
        )}
      <FadeAnimation animationRef={supportRef} className="h-full">
        {renderQuestionDetailSection()}
      </FadeAnimation>
    </div>
  );
}

export default withUndiagnosedSymptoms(withMedicalFlow(QMedicalSupportContent));
